<template>
    <div class="rounded-lg border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-800">
        <h4 class="mb-3 text-sm font-semibold text-gray-700 dark:text-zinc-300">
            {{ title }}
        </h4>

        <div class="space-y-2">
            <div v-if="party.name" class="text-sm font-medium text-gray-900 dark:text-zinc-100">
                <template v-if="editable">
                    <input
                        v-model="localParty.name"
                        type="text"
                        class="forms__input w-full"
                        :placeholder="lang('invoices::analysis.fields.name')"
                        @input="emit('update:party', localParty)"
                    />
                </template>
                <template v-else>{{ party.name }}</template>
            </div>

            <template v-if="editable">
                <div class="grid grid-cols-2 gap-2">
                    <input
                        v-model="localParty.tax_id"
                        type="text"
                        class="forms__input text-xs"
                        :placeholder="lang('invoices::analysis.fields.tax_id')"
                        @input="emit('update:party', localParty)"
                    />
                    <input
                        v-model="localParty.vat_id"
                        type="text"
                        class="forms__input text-xs"
                        :placeholder="lang('invoices::analysis.fields.vat_id')"
                        @input="emit('update:party', localParty)"
                    />
                </div>
                <input
                    v-model="localParty.street"
                    type="text"
                    class="forms__input text-xs w-full"
                    :placeholder="lang('invoices::analysis.fields.street')"
                    @input="emit('update:party', localParty)"
                />
                <div class="grid grid-cols-3 gap-2">
                    <input
                        v-model="localParty.postal_code"
                        type="text"
                        class="forms__input text-xs"
                        :placeholder="lang('invoices::analysis.fields.postal_code')"
                        @input="emit('update:party', localParty)"
                    />
                    <input
                        v-model="localParty.city"
                        type="text"
                        class="forms__input text-xs col-span-2"
                        :placeholder="lang('invoices::analysis.fields.city')"
                        @input="emit('update:party', localParty)"
                    />
                </div>
            </template>
            <template v-else>
                <div v-if="party.tax_id || party.vat_id" class="flex gap-3 text-xs text-gray-500 dark:text-zinc-400">
                    <span v-if="party.tax_id">{{ lang('invoices::analysis.fields.tax_id') }}: {{ party.tax_id }}</span>
                    <span v-if="party.vat_id">{{ lang('invoices::analysis.fields.vat_id') }}: {{ party.vat_id }}</span>
                </div>
                <div v-if="party.street" class="text-sm text-gray-600 dark:text-zinc-300">
                    {{ party.street }}
                </div>
                <div v-if="party.city || party.postal_code" class="text-sm text-gray-600 dark:text-zinc-300">
                    {{ [party.postal_code, party.city].filter(Boolean).join(' ') }}
                </div>
                <div v-if="party.email || party.phone" class="flex gap-3 text-xs text-gray-500 dark:text-zinc-400">
                    <span v-if="party.email">{{ party.email }}</span>
                    <span v-if="party.phone">{{ party.phone }}</span>
                </div>
            </template>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { reactive, watch } from 'vue';
import { lang } from '@/lang/composable';

interface Party {
    name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    street?: string | null;
    city?: string | null;
    postal_code?: string | null;
    tax_id?: string | null;
    vat_id?: string | null;
    email?: string | null;
    phone?: string | null;
    country_code?: string | null;
}

const props = withDefaults(defineProps<{
    title: string;
    party: Party;
    editable?: boolean;
}>(), {
    editable: false,
});

const emit = defineEmits<{
    'update:party': [party: Party];
}>();

const localParty = reactive({ ...props.party });

watch(() => props.party, (newParty) => {
    Object.assign(localParty, newParty);
}, { deep: true });
</script>
